import { type SelectedKey } from "@stacklet/ui";

/**
 * The shape of a combo item used by Autocomplete for role-assignment pickers.
 * Maps to the `id`/`name` contract Autocomplete expects.
 */
export interface RoleAssignmentComboItem extends SelectedKey {
  id: string;
  name: string;
  roleAssignmentTarget: string;
}

/**
 * The shape stored in the react-hook-form field value for role-assignment pickers.
 * Uses `label`/`value` to match the rest of the form serialisation conventions.
 */
export interface RoleAssignmentFieldValue {
  label: string;
  value: string;
  roleAssignmentTarget: string;
}

/**
 * Converts the react-hook-form field value into the Autocomplete `value` prop.
 *
 * Always returns a defined value (never undefined) so Autocomplete stays in
 * controlled mode even when the form field is empty or reset to null.
 */
export function toAutocompleteValue(
  fieldValue: unknown,
  multiple: boolean,
): RoleAssignmentComboItem | RoleAssignmentComboItem[] {
  if (!fieldValue) return [];

  if (multiple && Array.isArray(fieldValue)) {
    return (fieldValue as RoleAssignmentFieldValue[]).map((item) => ({
      id: item.value,
      name: item.label,
      roleAssignmentTarget: item.roleAssignmentTarget,
    }));
  }

  if (!multiple && !Array.isArray(fieldValue)) {
    const item = fieldValue as RoleAssignmentFieldValue;
    return {
      id: item.value,
      name: item.label,
      roleAssignmentTarget: item.roleAssignmentTarget,
    };
  }

  return [];
}

/**
 * Returns a Autocomplete `onChange` handler that converts the selected combo
 * item(s) back into the RoleAssignmentFieldValue shape expected by the form.
 */
export function createRoleAssignmentChangeHandler(
  fieldOnChange: (value: unknown) => void,
  multiple: boolean,
) {
  return (
    value: RoleAssignmentComboItem | RoleAssignmentComboItem[] | null,
  ) => {
    if (value === null || value === undefined) {
      fieldOnChange(multiple ? [] : null);
      return;
    }

    if (multiple && Array.isArray(value)) {
      fieldOnChange(
        value.map((item) => ({
          label: item.name,
          value: item.id,
          roleAssignmentTarget: item.roleAssignmentTarget,
        })),
      );
    } else if (!multiple && !Array.isArray(value)) {
      fieldOnChange({
        label: value.name,
        value: value.id,
        roleAssignmentTarget: value.roleAssignmentTarget,
      });
    }
  };
}
